import React, { type ReactNode } from "react";

import {
  DOMNode,
  domToReact,
  Element as DomElement,
  attributesToProps,
} from "html-react-parser";

import { getHtmlTagStyles } from "../utils/html-tag-styles";

/**
 * 일반 태그에 스타일 적용하여 렌더링하는 helper 함수
 *
 * - `element`: html-react-parser가 넘겨주는 DOM Element 정보
 * - `index`: 현재 element가 동일 depth에서 몇 번째 child 인지에 대한 index
 *            → React key 생성에 사용되어 동일 부모 내에서 key가 unique 해지도록 함
 * - `transform`: 상위에서 내려온 transform 함수(html-react-parser용)를 그대로 다시 호출해서
 *                children 도 동일한 규칙으로 재귀적으로 변환할 수 있게 함
 */
/**
 * 비표준 커스텀 태그를 표준 HTML 태그로 매핑
 *
 * React는 비표준 태그(예: <chart_type>)를 렌더링할 때 경고를 발생시킨다.
 * 이 매핑을 통해:
 * 1. 커스텀 태그 → 표준 HTML 태그로 변환하여 React 경고 방지
 * 2. 스타일은 원본 태그 이름 기준으로 적용 (getHtmlTagStyles)
 *
 * - chart_type: 차트 유형 배지 → inline 요소인 <span>으로 렌더링
 * - chart_description: 차트 설명 텍스트 → block 요소인 <p>로 렌더링
 * - figurecaption: 모델이 생성한 오타 → 표준 <figcaption>으로 정규화
 */
const customTagMap: Record<string, string> = {
  chart_type: "span",
  chart_description: "p",
  figurecaption: "figcaption",
};

export function renderStyledElement(
  element: DomElement,
  index: number,
  transform: (
    reactNode: ReactNode,
    domNode: DOMNode,
    nodeIndex: number,
  ) => React.ReactElement | null | undefined,
): React.ReactElement {
  // 커스텀 태그는 표준 태그로 변환 (스타일은 원래 태그 기준으로 적용)
  const originalTagName = element.name;
  const renderTagName = customTagMap[originalTagName] ?? originalTagName;

  // 태그 이름과 class 속성에 따라 Tailwind 기반 스타일 클래스 배열을 가져온다.
  // (현재 API 는 캡션 역할을 class 로 내려주므로 class 도 함께 넘긴다)
  const className = getHtmlTagStyles(originalTagName, element.attribs?.class);

  // 원본 HTML 속성에서 style을 제외한 속성들을 추출
  // (style은 Tailwind 클래스로 대체하므로 제외)
  const { style: _style, ...attribsWithoutStyle } = element.attribs || {};

  // HTML 속성을 React props로 변환 (예: colspan → colSpan, class → className)
  const convertedAttribs = attributesToProps(attribsWithoutStyle);

  // 현재 element에 원본 속성 + 공통 스타일(className) + 고유 key를 부여한다.
  // key는 React의 reconciliation 시 동일 형제 노드를 안정적으로 식별하기 위해 필요하다.
  const props = {
    ...convertedAttribs,
    className: [convertedAttribs.className, ...className]
      .filter(Boolean)
      .join(" "),
    key: `element-${element.name}-${index}`,
  };

  // React에서는 void element(hr, br, img 등)에 children을 줄 수 없으므로 분기 처리
  const voidElements = new Set([
    "area",
    "base",
    "br",
    "col",
    "embed",
    "hr",
    "img",
    "input",
    "link",
    "meta",
    "param",
    "source",
    "track",
    "wbr",
  ]);

  if (voidElements.has(renderTagName)) {
    return React.createElement(renderTagName, props);
  }

  // 그 외의 일반 태그는 children 을 다시 domToReact 로 파싱하면서
  // html-react-parser 의 transform(reactNode, domNode, index) 시그니처를 그대로 따라간다.
  const children = domToReact((element.children as DOMNode[]) || [], {
    transform(reactNode, domNode, childIndex) {
      // 상위에서 정의한 transform 을 다시 호출하여
      // 특수 케이스(equation, chart, figure, caption 등)를 동일하게 적용한다.
      const child = transform(reactNode, domNode, childIndex);

      // transform 결과가 ReactElement 이고 아직 key 가 없다면,
      // 부모 element 정보 + childIndex 를 조합하여 child 에도 고유 key를 부여한다.
      // 이렇게 하면 <tr> 안의 <td> 처럼 반복되는 구조에서도
      // "Encountered two children with the same key" 경고를 피할 수 있다.
      if (React.isValidElement(child) && child.key == null) {
        return React.cloneElement(child, {
          key: `child-${element.name}-${index}-${childIndex}`,
        });
      }
      return child;
    },
  });

  return React.createElement(renderTagName, props, children);
}
